import { z } from 'zod';
import { defineEnum, type Tone } from './enums';
import { dateSchema, dateTimeSchema, optionalId } from './fields';
import { idSchema } from './ids';
import { jobStateSchema } from './jira';

/**
 * Biznesa aplis (FR-BIZ-001…017): 13 steps around the wheel, the production steps 9.1–9.5 in the
 * inner circle, four colour groups. Codes are PMAI's ("9.1"), stored as they are.
 */
export const STEP_GROUPS = ['sales', 'deal', 'prod', 'care'] as const;
export type StepGroup = (typeof STEP_GROUPS)[number];
const GROUP_TONE: Record<StepGroup, Tone> = {
  sales: 'info',
  deal: 'warning',
  prod: 'success',
  care: 'accent',
};

const STEPS = [
  ['1', 'care'],
  ['2', 'sales'],
  ['3', 'sales'],
  ['4', 'deal'],
  ['5', 'deal'],
  ['6', 'deal'],
  ['7', 'prod'],
  ['8', 'prod'],
  ['9', 'prod'],
  ['9.1', 'prod'],
  ['9.2', 'prod'],
  ['9.3', 'prod'],
  ['9.4', 'prod'],
  ['9.5', 'prod'],
  ['10', 'deal'],
  ['11', 'deal'],
  ['12', 'care'],
  ['13', 'care'],
] as const satisfies readonly (readonly [string, StepGroup])[];

/** In wheel order: 1…9, 9.1…9.5, 10…13 (the order of groups and selects). */
export const BusinessSteps = defineEnum(
  'enum.businessStep',
  STEPS.map(([code, group]) => [code, GROUP_TONE[group]] as const),
  (code) => code.replace('.', '_'),
);
export type BusinessStep = (typeof BusinessSteps.codes)[number];
export const stepGroup = (step: BusinessStep): StepGroup => STEPS.find(([c]) => c === step)![1];
/** The outer ring (13) and the production circle (9.1–9.5). */
export const OUTER_STEPS = BusinessSteps.codes.filter((c) => !c.includes('.'));
export const PRODUCTION_STEPS = BusinessSteps.codes.filter((c) => c.includes('.'));
/** A task without a step counts as development (FR-BIZ-010). */
export const DEFAULT_STEP: BusinessStep = '9.3';
/** Step 1 shows competences (technologies), not Jira tasks (FR-BIZ-011). */
export const COMPETENCE_STEP: BusinessStep = '1';

export interface StepInput {
  summary: string;
  labels: readonly string[];
  statusName: string;
  /** Jira status category key: new | indeterminate | done. */
  statusCategory: string;
  projectKey: string | null;
}

const KEYWORDS: readonly [RegExp, BusinessStep][] = [
  [/(kick ?off|kick-?off)/, '7'],
  [/(t[āa]m[ēe]|estimat|nov[ēe]rt[ēe]|pied[āa]v[āa]j|\boffer\b|\bquote\b)/, '4'],
  [/(l[īi]gum|contract|\bsow\b|\bdu-?\d)/, '5'],
  [/(avans|advance payment)/, '6'],
  [/(apmaks|\bpayment\b|overdue|neapmaks|r[ēe][ķk]in.*kav)/, '11'],
  [/(piekļuv|\baccess\b|\bvpn\b|credential|permission|\bparol|\blogin\b|tiesīb)/, '8'],
  [/(apm[āa]c[īi]b|\btraining\b|retrospect|lessons learned)/, '13'],
  [/(\bpna\b|gala nodoš|final hand|acceptance act|pieņemšanas akt)/, '10'],
];

/**
 * The proposed step of a Jira task (FR-BIZ-006, PMAI `proposeStep`): support first, then keyword
 * rules on the summary and labels, then the Jira status. `project.type` exists only in migrated
 * PMAI data (no UI sets it).
 */
export function proposeStep(
  issue: StepInput,
  project: { name: string; type?: string | null } | null,
): BusinessStep {
  const status = issue.statusName.toLowerCase();
  const category = issue.statusCategory.toLowerCase();
  const labels = issue.labels.map((l) => l.toLowerCase());
  const text = `${issue.summary} ${labels.join(' ')}`.toLowerCase();
  if (
    issue.projectKey === 'SUP' ||
    (project?.type ?? '').toLowerCase().includes('atbalst') ||
    (project?.name ?? '').toLowerCase().includes('support') ||
    labels.includes('support') ||
    labels.includes('maintenance')
  )
    return '12';
  for (const [pattern, step] of KEYWORDS) if (pattern.test(text)) return step;
  if (
    /(ready.*(release|deploy|deliver)|to ?deploy|\buat\b|accept|nodoš|done pending|staging|\brelease\b)/.test(
      status,
    )
  )
    return '9.5';
  if (/(in ?review|\breview\b|test|\bqa\b|verif|p[āa]rbaud)/.test(status)) return '9.4';
  if (/(anal[īi]z|analy|izp[ēe]t|discovery|refin|groom|izv[ēe]rt)/.test(text)) return '9.2';
  if (category === 'indeterminate' || /(in ?progress|izstr[āa]d|develop|\bdev\b)/.test(status)) return '9.3';
  if (category === 'new' || /(to ?do|^open$|backlog|selected|^new$|todo|jauns|atv[ēe]rt|defin)/.test(status))
    return '9.1';
  return '9.3';
}

// ───────────────────────────── API ─────────────────────────────

export const jiraIssueSchema = z.object({
  key: z.string(),
  summary: z.string(),
  issueType: z.string(),
  statusName: z.string(),
  /** Assignee's display name; "Nepiešķirts" when none. */
  assigneeName: z.string().nullable(),
  dueDate: dateSchema.nullable(),
  priority: z.string().nullable(),
  labels: z.array(z.string()),
  jiraUpdated: dateTimeSchema.nullable(),
  projectId: idSchema.nullable(),
  /** Customer of the mapped project (client filter, FR-BIZ-012). */
  customerId: idSchema.nullable(),
  proposedStep: BusinessSteps.schema,
  step: BusinessSteps.schema,
  approved: z.boolean(),
  burning: z.boolean(),
});
export type JiraIssue = z.infer<typeof jiraIssueSchema>;

/** "Ievilkti N · jauni · atjaunināti · noņemti · gaida" (FR-BIZ-002). */
export const issueSyncResultSchema = z.object({
  pulled: z.number().int(),
  added: z.number().int(),
  updated: z.number().int(),
  removed: z.number().int(),
  pending: z.number().int(),
});
export type IssueSyncResult = z.infer<typeof issueSyncResultSchema>;
export const issueJobStateSchema = jobStateSchema(issueSyncResultSchema);
export type IssueJobState = z.infer<typeof issueJobStateSchema>;

/** Changing step, project or "degošs" of a task (FR-BIZ-008, FR-BIZ-015). */
export const jiraIssueUpdateSchema = z.object({
  step: BusinessSteps.schema.optional(),
  projectId: optionalId,
  burning: z.boolean().optional(),
});

/** "Apstiprināt visus" of a step group (FR-BIZ-009). */
export const jiraIssueApproveSchema = z.object({ keys: z.array(z.string().min(1).max(50)).min(1).max(1000) });

/** Jira priorities, highest first (FR-BIZ-016); unknown ones sort as Medium. */
export const PRIORITY_ORDER = ['highest', 'high', 'medium', 'low', 'lowest'] as const;
export const jiraPriorityRank = (priority: string | null): number => {
  const i = PRIORITY_ORDER.indexOf((priority ?? '').toLowerCase() as (typeof PRIORITY_ORDER)[number]);
  return i === -1 ? 2 : i;
};

/** A live project as the wheel needs it: client filter and competences (FR-BIZ-011/012). */
export const bizcycleProjectSchema = z.object({
  id: idSchema,
  name: z.string(),
  customerId: idSchema,
  customerName: z.string(),
  technologyIds: z.array(idSchema),
});
export type BizcycleProject = z.infer<typeof bizcycleProjectSchema>;
